<template>
  <section class="sa-company-progress" aria-label="Andamento do cliente">
    <header class="sa-company-progress__header">
      <div class="sa-company-progress__lead">
        <h2 class="sa-company-progress__title">Andamento do cliente</h2>
        <p class="sa-company-progress__subtitle">
          Visão rápida de uso, vendas e configuração para quem acompanha este cliente.
        </p>
      </div>
      <div class="sa-company-progress__actions">
        <span v-if="isMock" class="sa-company-progress__badge">Dados preview</span>
        <button
          type="button"
          class="btnSave sa-company-progress__export"
          :disabled="exporting"
          @click="exportXls"
        >
          {{ exporting ? 'Exportando...' : 'Exportar XLS' }}
        </button>
      </div>
    </header>

    <ReportsSummary :metrics="kpiMetrics" aria-label="Indicadores do cliente" />

    <div class="sa-company-progress__charts">
      <DashboardChartCard
        title="Receita (6 meses)"
        subtitle="Evolução mensal de faturamento"
        emphasis
      >
        <VueApexCharts
          type="area"
          height="220"
          width="100%"
          :options="revenueChartOptions"
          :series="revenueChartSeries"
        />
      </DashboardChartCard>

      <DashboardChartCard
        title="Vendas e alunos"
        subtitle="Comparativo mensal"
      >
        <VueApexCharts
          type="bar"
          height="220"
          width="100%"
          :options="activityChartOptions"
          :series="activityChartSeries"
        />
      </DashboardChartCard>

      <DashboardChartCard
        title="Configuração"
        subtitle="Itens concluídos vs pendentes"
      >
        <VueApexCharts
          type="donut"
          height="220"
          width="100%"
          :options="checklistChartOptions"
          :series="checklistChartSeries"
        />
      </DashboardChartCard>
    </div>

    <div class="sa-company-progress__completion">
      <div class="sa-company-progress__completion-meta">
        <strong>{{ percent }}% configurado</strong>
        <span>{{ doneCount }} de {{ total }} itens concluídos</span>
      </div>
      <div
        class="sa-company-progress__bar"
        role="progressbar"
        :aria-valuenow="percent"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="`Progresso de configuração: ${percent}%`"
      >
        <span class="sa-company-progress__bar-fill" :style="{ width: `${percent}%` }" />
      </div>
    </div>

    <ul class="sa-company-progress__checklist" aria-label="Checklist de configuração">
      <li
        v-for="item in items"
        :key="item.id"
        class="sa-company-progress__item"
        :class="{ 'is-done': item.done, 'is-pending': !item.done }"
      >
        <span class="sa-company-progress__check" aria-hidden="true">
          <fa v-if="item.done" icon="fa-solid fa-circle-check" />
          <fa v-else icon="fa-regular fa-circle" />
        </span>
        <div class="sa-company-progress__item-copy">
          <strong>{{ item.label }}</strong>
          <p>{{ item.hint }}</p>
        </div>
        <span class="sa-company-progress__status">
          {{ item.done ? 'Concluído' : 'Pendente' }}
        </span>
      </li>
    </ul>
  </section>
</template>

<script>
import ReportsSummary from '@/components/Weflix/SuperAdmin/Reports/ReportsSummary.vue';
import DashboardChartCard from '@/components/Weflix/SuperAdmin/Dashboard/DashboardChartCard.vue';
import { formatCurrencyBRL, formatNumberBR } from '@/utils/superAdmin/formatters';
import { exportCompanyOverviewXls } from '@/utils/superAdmin/companyOverview';

export default {
  name: 'CompanyProgressPanel',
  components: { ReportsSummary, DashboardChartCard },
  props: {
    metrics: {
      type: Object,
      default: () => ({
        students: 0,
        courses: 0,
        sales: 0,
        revenue: 0,
        isMock: true,
      }),
    },
    checklist: {
      type: Object,
      default: () => ({
        items: [],
        doneCount: 0,
        total: 0,
        percent: 0,
      }),
    },
    charts: {
      type: Object,
      default: () => ({
        months: [],
        revenueTrend: [],
        salesTrend: [],
        studentsTrend: [],
      }),
    },
    companyName: { type: String, default: '' },
    companyId: { type: [Number, String], default: null },
    isMock: { type: Boolean, default: true },
  },
  data() {
    return {
      exporting: false,
    };
  },
  computed: {
    items() {
      return Array.isArray(this.checklist?.items) ? this.checklist.items : [];
    },
    doneCount() {
      return Number(this.checklist?.doneCount) || 0;
    },
    total() {
      return Number(this.checklist?.total) || 0;
    },
    percent() {
      return Number(this.checklist?.percent) || 0;
    },
    kpiMetrics() {
      const m = this.metrics || {};
      return [
        { label: 'Alunos', value: formatNumberBR(m.students || 0) },
        { label: 'Cursos', value: formatNumberBR(m.courses || 0) },
        { label: 'Vendas', value: formatNumberBR(m.sales || 0) },
        { label: 'Faturamento', value: formatCurrencyBRL(m.revenue || 0) },
      ];
    },
    chartMonths() {
      return Array.isArray(this.charts?.months) && this.charts.months.length
        ? this.charts.months
        : (this.charts?.revenueTrend || []).map((row) => row.label);
    },
    revenueChartSeries() {
      return [{
        name: 'Receita',
        data: (this.charts?.revenueTrend || []).map((row) => Number(row.value) || 0),
      }];
    },
    revenueChartOptions() {
      return {
        chart: {
          type: 'area',
          fontFamily: 'Inter, system-ui, sans-serif',
          toolbar: { show: false },
          animations: { enabled: true, speed: 400 },
        },
        colors: ['#250891'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.05,
            stops: [0, 90, 100],
          },
        },
        grid: {
          borderColor: '#eef0f4',
          strokeDashArray: 4,
        },
        xaxis: {
          categories: this.chartMonths,
          labels: { style: { colors: '#697386', fontSize: '11px' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: '#697386', fontSize: '11px' },
            formatter: (val) => formatCompactAxis(val),
          },
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrencyBRL(val),
          },
        },
      };
    },
    activityChartSeries() {
      return [
        {
          name: 'Vendas',
          data: (this.charts?.salesTrend || []).map((row) => Number(row.value) || 0),
        },
        {
          name: 'Alunos',
          data: (this.charts?.studentsTrend || []).map((row) => Number(row.value) || 0),
        },
      ];
    },
    activityChartOptions() {
      return {
        chart: {
          type: 'bar',
          fontFamily: 'Inter, system-ui, sans-serif',
          toolbar: { show: false },
          animations: { enabled: true, speed: 400 },
        },
        colors: ['#1AC7D0', '#250891'],
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '55%',
          },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: '#eef0f4',
          strokeDashArray: 4,
        },
        xaxis: {
          categories: this.chartMonths,
          labels: { style: { colors: '#697386', fontSize: '11px' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: '#697386', fontSize: '11px' },
            formatter: (val) => formatNumberBR(val),
          },
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left',
          fontSize: '12px',
          markers: { radius: 10 },
        },
        tooltip: {
          y: {
            formatter: (val) => formatNumberBR(val),
          },
        },
      };
    },
    checklistChartSeries() {
      const pending = Math.max(0, this.total - this.doneCount);
      return [this.doneCount, pending];
    },
    checklistChartOptions() {
      return {
        chart: {
          type: 'donut',
          fontFamily: 'Inter, system-ui, sans-serif',
          animations: { enabled: true, speed: 400 },
        },
        labels: ['Concluído', 'Pendente'],
        colors: ['#15803d', '#d5d9e2'],
        legend: { show: false },
        dataLabels: { enabled: false },
        stroke: { width: 2, colors: ['#fff'] },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#697386',
                  offsetY: 16,
                },
                value: {
                  show: true,
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#191c24',
                  offsetY: -8,
                  formatter: () => `${this.percent}%`,
                },
                total: {
                  show: true,
                  label: 'Progresso',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#697386',
                  formatter: () => `${this.percent}%`,
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${formatNumberBR(val)} itens`,
          },
        },
      };
    },
  },
  methods: {
    exportXls() {
      if (this.exporting) return;
      this.exporting = true;
      try {
        exportCompanyOverviewXls(
          {
            metrics: this.metrics,
            checklist: this.checklist,
            charts: this.charts,
            companyName: this.companyName,
            companyId: this.companyId,
          },
          { name: this.companyName, id: this.companyId },
        );
      } finally {
        this.exporting = false;
      }
    },
  },
};

function formatCompactAxis(value) {
  const amount = Number(value) || 0;
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)} mi`;
  if (amount >= 1000) return `${Math.round(amount / 1000)}k`;
  return formatNumberBR(amount);
}
</script>

<style scoped>
.sa-company-progress {
  --dashboard-border: #e8eaf0;
  --dashboard-text: #191c24;
  --dashboard-muted: #697386;
  --dashboard-primary: #250891;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 0;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  text-align: left;
}

.sa-company-progress__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem 1rem;
}

.sa-company-progress__title {
  margin: 0 0 0.25rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--dashboard-text);
  line-height: 1.3;
}

.sa-company-progress__subtitle {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--dashboard-muted);
}

.sa-company-progress__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
}

.sa-company-progress__badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #9a6700;
  background: #fff7e6;
  border: 1px solid #f3e0b8;
}

.sa-company-progress__export {
  min-height: 40px;
  padding: 0.55rem 0.95rem;
  font-size: 0.8125rem;
}

.sa-company-progress__charts {
  display: grid;
  grid-template-columns: 1.4fr 1.2fr 0.9fr;
  gap: 0.85rem;
}

.sa-company-progress__completion {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.sa-company-progress__completion-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.35rem 1rem;
  font-size: 0.8125rem;
  color: var(--dashboard-muted);
}

.sa-company-progress__completion-meta strong {
  color: var(--dashboard-text);
  font-weight: 700;
}

.sa-company-progress__bar {
  height: 8px;
  border-radius: 999px;
  background: #eef0f5;
  overflow: hidden;
}

.sa-company-progress__bar-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #250891 0%, #4b2fd4 100%);
  transition: width 220ms ease;
}

.sa-company-progress__checklist {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
}

.sa-company-progress__item {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid var(--dashboard-border);
  border-radius: 10px;
  background: #f8f9fc;
}

.sa-company-progress__item.is-done {
  background: #f3faf6;
  border-color: #d6ebe0;
}

.sa-company-progress__check {
  flex-shrink: 0;
  margin-top: 0.1rem;
  color: #a0a6b4;
  font-size: 1rem;
  line-height: 1;
}

.sa-company-progress__item.is-done .sa-company-progress__check {
  color: #15803d;
}

.sa-company-progress__item-copy {
  flex: 1;
  min-width: 0;
}

.sa-company-progress__item-copy strong {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--dashboard-text);
}

.sa-company-progress__item-copy p {
  margin: 0.15rem 0 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--dashboard-muted);
}

.sa-company-progress__status {
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #9a6700;
}

.sa-company-progress__item.is-done .sa-company-progress__status {
  color: #15803d;
}

@media (max-width: 1100px) {
  .sa-company-progress__charts {
    grid-template-columns: 1fr 1fr;
  }

  .sa-company-progress__charts > :last-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 900px) {
  .sa-company-progress__checklist {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .sa-company-progress__charts {
    grid-template-columns: 1fr;
  }

  .sa-company-progress__charts > :last-child {
    grid-column: auto;
  }
}
</style>
